import { Box, FlexBox, Skeleton, Text } from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import { formatInstrumentNumber, MetricInstrumentData } from 'lib-analytics-ui';
import { ResponseTimeMetric } from 'lib-dashboard-types';
import { QueueThresholds } from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { memo, useMemo } from 'react';
import { MarkRequired } from 'ts-essentials';

import {
  QueueDashboardMetric,
  QueueDashboardMetricProps,
} from 'app/views/layout/views/dashboard/views/queue/components/queues/components/metric';

import { useQueueDashboardQueueContext } from '../../../../../queue/queueDashboardQueue.context';

type QueueDashboardSLAResponseTimeMetricProps = {
  metric?: ResponseTimeMetric;
} & PropsWithClassName;

export const QueueDashboardSLAResponseTimeMetric = memo(
  function QueueDashboardSLAResponseTimeMetric({
    className,
    metric,
  }: QueueDashboardSLAResponseTimeMetricProps): JSX.Element {
    const { queue, isSkeleton } = useQueueDashboardQueueContext();

    const threshold = (queue?.queue_thresholds ?? {}) as MarkRequired<
      QueueThresholds,
      'sla_percentage' | 'sla_time_limit'
    >;

    const thresholdAlertTooltip =
      useMemo((): QueueDashboardMetricProps['alertTooltip'] => {
        if (!metric?.threshold_breached || !threshold?.sla_percentage) {
          return null;
        }

        return {
          head: 'Service-level alert',
          content: (
            <>
              This queue is below the goal of <b>{threshold.sla_percentage}%</b>{' '}
              conversations answered within{' '}
              <b>
                {formatInstrumentNumber(
                  threshold.sla_time_limit * 1000,
                  'duration',
                )}
              </b>
              .
            </>
          ),
        };
      }, [metric, threshold.sla_percentage, threshold.sla_time_limit]);

    return (
      <QueueDashboardMetric
        className={className}
        metricType="response_time"
        title="Response time SLA"
        isSkeleton={isSkeleton}
        alertTooltip={thresholdAlertTooltip}
      >
        {isSkeleton ? (
          <Skeleton height="24px" width="72px" />
        ) : (
          <FlexBox flexDirection="column" gap="200">
            <Text.Paragraph variant="4" subdued>
              Avg. response time:{' '}
              <Text.Paragraph
                variant="3"
                as="span"
                color={
                  !metric?.average_response_time
                    ? typography.color.tertiary
                    : undefined
                }
              >
                {metric?.average_response_time
                  ? formatInstrumentNumber(
                      metric.average_response_time,
                      'duration',
                    )
                  : 'n.a.'}
              </Text.Paragraph>
            </Text.Paragraph>

            <Box alignSelf="center">
              <MetricInstrumentData
                data={metric?.sla_percentage ?? null}
                format="percentage"
              />
            </Box>

            <div>
              <svg width="100%" height="18px">
                <text
                  x="0%"
                  y="50%"
                  dy="4"
                  fontSize={typography.size[200]}
                  fill={typography.color.tertiary}
                >
                  0
                </text>

                <svg x="6%" width="75%" style={{ overflow: 'visible' }}>
                  <line
                    x1="0%"
                    x2="100%"
                    y1="50%"
                    y2="50%"
                    stroke={colors.neutral[400]}
                    strokeWidth="1"
                    strokeDasharray="3,2"
                  />

                  <line
                    x1={`${threshold.sla_percentage}%`}
                    x2={`${threshold.sla_percentage}%`}
                    y1="30%"
                    y2="100%"
                    stroke={colors.neutral[400]}
                  />

                  {metric?.sla_percentage && (
                    <circle
                      cx={`${metric.sla_percentage}%`}
                      cy="50%"
                      r="3"
                      fill="white"
                      stroke={
                        metric.sla_percentage >= threshold.sla_percentage
                          ? colors.green[500]
                          : colors.orange[600]
                      }
                      strokeWidth="1"
                    />
                  )}
                </svg>

                <text
                  x="100%"
                  y="50%"
                  dy="4"
                  fontSize={typography.size[200]}
                  textAnchor="end"
                  fill={typography.color.tertiary}
                >
                  100%
                </text>
              </svg>

              <Text.Paragraph variant="3" color="tertiary" align="right">
                Goal {threshold.sla_percentage}% in{' '}
                {formatInstrumentNumber(
                  threshold.sla_time_limit * 1000,
                  'duration',
                )}
              </Text.Paragraph>
            </div>
          </FlexBox>
        )}
      </QueueDashboardMetric>
    );
  },
);
